/**
 * SceneHookHeading — shared primitive for the 3-line "dream hook" heading
 * pattern used across plan/niche-v3 scene components.
 *
 * Two exports:
 *   1. `sceneHookHeadingClasses({ fluid, tracking })` — class string for use
 *      inside `cn(...)` next to per-line motion wrappers (motion.h3 / motion.p)
 *      and per-line color overrides. Most callsites use this because each
 *      line gets its own staggered animation transition.
 *   2. `<SceneHookHeading>` — full component for simple inline cases without
 *      per-line motion override.
 *
 * Two visual variants:
 *   - default: `text-[32px] sm:text-4xl md:text-5xl lg:text-6xl` (4 plan
 *     scenes + niche-v3 CustomerScene).
 *   - fluid:   `text-[clamp(2rem,6vw,3.75rem)]` (MathScene, ProblemCustomerScene).
 *
 * Why no motion props passthrough: each line in the 3-line stagger has its
 * own `delay` (0 / 0.2s / 0.45s) and color (`text-white` / `text-white/55` /
 * amber gradient). Wrapping motion would explode the prop surface; instead
 * callsites keep their motion.h3/motion.p and reuse the class string.
 */

import { cn } from "@/lib/utils";
import type { HTMLAttributes, ReactNode } from "react";

interface SceneHookHeadingClassOptions {
  /** Use fluid `clamp(2rem, 6vw, 3.75rem)` sizing instead of breakpoint ladder. */
  fluid?: boolean;
  /** Add `tracking-tight` (Battlefield/Weapon/RealityCheck/Math). Default false. */
  tracking?: boolean;
}

/**
 * Class string for the 3-line "dream hook" heading.
 *
 * @example
 *   const base = sceneHookHeadingClasses({ tracking: true });
 *   <motion.p className={cn(base, "text-white tabular-nums")}>...</motion.p>
 */
export function sceneHookHeadingClasses({
  fluid = false,
  tracking = false,
}: SceneHookHeadingClassOptions = {}): string {
  return cn(
    "font-heading font-bold leading-[1.05]",
    fluid
      ? "text-[clamp(2rem,6vw,3.75rem)]"
      : "text-[32px] sm:text-4xl md:text-5xl lg:text-6xl",
    tracking && "tracking-tight",
  );
}

interface SceneHookHeadingProps
  extends Omit<HTMLAttributes<HTMLHeadingElement>, "children"> {
  children: ReactNode;
  /** Heading level. Default: `h3` (matches existing usage). */
  as?: "h1" | "h2" | "h3";
  /** Use fluid `clamp(...)` sizing instead of breakpoint ladder. */
  fluid?: boolean;
  /** Add `tracking-tight`. Default false. */
  tracking?: boolean;
}

/**
 * Full component variant. Use for simple inline cases without per-line motion
 * override. For motion-wrapped lines, prefer `sceneHookHeadingClasses()` +
 * `cn()` so the caller keeps full control over `motion.h3` / `motion.p`.
 */
export function SceneHookHeading({
  children,
  as: Tag = "h3",
  fluid = false,
  tracking = false,
  className,
  ...rest
}: SceneHookHeadingProps) {
  return (
    <Tag
      className={cn(sceneHookHeadingClasses({ fluid, tracking }), className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}
